import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MoreHorizontal } from 'lucide-react';
import { Button } from './Button';
import { useIsMobile } from '../../hooks/useIsMobile';

export interface ActionDef {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'ghost' | 'secondary';
  danger?: boolean;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  backTo?: string;
  badge?: React.ReactNode;
  metadata?: React.ReactNode;
  actions?: ActionDef[];
  children?: React.ReactNode;
  onTitleChange?: (title: string) => void;
}

export function PageHeader({ title, subtitle, backTo, badge, metadata, actions, children, onTitleChange }: PageHeaderProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [menuOpen]);

  const visibleActions = isMobile && actions ? actions.slice(0, 2) : (actions ?? []);
  const overflowActions = isMobile && actions ? actions.slice(2) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '100%' }}>
      {/* Back button */}
      {backTo && (
        <button
          onClick={() => navigate(backTo)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)',
            display: 'flex', alignItems: 'center', gap: '0.375rem',
            padding: 0, alignSelf: 'flex-start',
          }}
        >
          <ArrowLeft size={14} /> Indietro
        </button>
      )}

      {/* Header row */}
      <div
        className="animate-in"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.75rem',
          maxWidth: '100%',
        }}
      >
        {/* Left side */}
        <div style={{ minWidth: 0, maxWidth: '100%', flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', minWidth: 0 }}>
            {badge}
            {editing ? (
              <input
                ref={inputRef}
                aria-label="Modifica titolo"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={() => {
                  const trimmed = draft.trim();
                  if (trimmed && trimmed !== title) onTitleChange?.(trimmed);
                  else setDraft(title);
                  setEditing(false);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                  if (e.key === 'Escape') {
                    setDraft(title);
                    setEditing(false);
                  }
                }}
                 style={{
                   fontSize: 'var(--text-xl)',
                   fontWeight: 700,
                   fontFamily: 'inherit',
                   lineHeight: 1.1,
                   color: 'var(--color-text)',
                   background: 'transparent',
                   border: 'none',
                   borderBottom: '2px solid var(--color-primary)',
                   outline: 'none',
                   padding: 0,
                   margin: 0,
                   width: '100%',
                   maxWidth: '100%',
                   borderRadius: 0,
                   boxSizing: 'border-box',
                   WebkitAppearance: 'none',
                 }}
              />
            ) : (
              <h1
                tabIndex={onTitleChange ? 0 : undefined}
                role={onTitleChange ? 'button' : undefined}
                onClick={onTitleChange ? () => { setDraft(title); setEditing(true); } : undefined}
                onKeyDown={onTitleChange ? (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDraft(title);
                    setEditing(true);
                  }
                } : undefined}
                style={{
                  fontSize: 'var(--text-xl)',
                  lineHeight: 1.1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  ...(onTitleChange ? { cursor: 'pointer' } : {}),
                }}
                title={onTitleChange ? 'Clicca per modificare' : undefined}
              >
                {title}
              </h1>
            )}
          </div>
          {subtitle && (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
              {subtitle}
            </p>
          )}
          {metadata && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.25rem', minWidth: 0 }}>
              {metadata}
            </div>
          )}
        </div>

        {/* Right side: actions */}
        {actions && actions.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', maxWidth: '100%', flexShrink: 0 }}>
            {visibleActions.map((a, i) => (
              <Button
                key={i}
                size="sm"
                variant={a.variant ?? 'ghost'}
                icon={a.icon}
                onClick={a.onClick}
                style={a.danger ? { color: 'var(--color-error)', borderColor: 'var(--color-error)' } : undefined}
              >
                {isMobile ? undefined : a.label}
              </Button>
            ))}
            {overflowActions.length > 0 && (
              <div ref={menuRef} style={{ position: 'relative' }}>
                <Button size="sm" variant="ghost" icon={<MoreHorizontal size={14} />} onClick={() => setMenuOpen(!menuOpen)} aria-label="Altre azioni" />
                {menuOpen && (
                  <div style={{
                    position: 'absolute', right: 0, top: '100%', zIndex: 50,
                    background: 'var(--glass-bg-elevated)',
                    backdropFilter: 'blur(20px) saturate(160%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 'var(--radius-card)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                    minWidth: 180, padding: '0.25rem', marginTop: '0.25rem',
                  }}>
                    {overflowActions.map((a, i) => (
                      <button
                        key={i}
                        onClick={() => { a.onClick(); setMenuOpen(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          width: '100%', padding: '0.5rem 0.75rem',
                          background: 'none', border: 'none', borderRadius: 'var(--radius-interactive)',
                          color: a.danger ? 'var(--color-error)' : 'var(--color-text)',
                          fontSize: 'var(--text-sm)', cursor: 'pointer',
                        }}
                      >
                        {a.icon}
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Extra content */}
      {children}
    </div>
  );
}
